import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { studentsAPI, lessonsAPI, programsAPI, locationsAPI, coachesAPI, emailNotificationsAPI, certificatesAPI, enrollmentsAPI, lessonRequestsAPI } from '../lib/api';
import { dispatchActivityRefresh } from '../lib/activityRefresh';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { PhotoUpload } from '../components/PhotoUpload';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Download,
  CheckCircle,
  Clock,
  Calendar,
  User,
  Phone,
  MapPin,
  Award,
  Loader2,
  Plus,
  Mail,
  ShieldCheck,
  ShieldX,
  FileCheck,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  CheckCircle2,
  Circle,
  Briefcase,
  Waves,
  Heart,
  Trophy,
  PartyPopper,
  XCircle,
  RotateCcw,
  CalendarClock,
  MoreVertical,
  History,
  GraduationCap,
  RefreshCw,
  MessageSquare,
  AlertTriangle,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canEdit, isAdmin } = useAuth();
  const [student, setStudent] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    lesson_count: 5,
    start_date: '',
    interval_days: 7,
    coach_id: '',
    location_id: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Certificate state
  const [certificateStatus, setCertificateStatus] = useState(null);
  const [approvingCert, setApprovingCert] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [downloadingCert, setDownloadingCert] = useState(false);
  
  // Lesson management state
  const [editLessonDialogOpen, setEditLessonDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [cancelLessonDialogOpen, setCancelLessonDialogOpen] = useState(false);
  const [deleteLessonDialogOpen, setDeleteLessonDialogOpen] = useState(false);
  const [lessonFormData, setLessonFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    coach_id: '',
    location_id: '',
    notes: '',
  });

  // Enrollment state (Phase 3 & 4)
  const [enrollments, setEnrollments] = useState([]);
  const [activeEnrollment, setActiveEnrollment] = useState(null);
  const [newEnrollmentDialogOpen, setNewEnrollmentDialogOpen] = useState(false);
  const [enrollmentFormData, setEnrollmentFormData] = useState({
    program_id: '',
    sessions_total: 10,
    start_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Lesson Change Request state (Safe Lesson Control)
  const [lessonRequests, setLessonRequests] = useState([]);
  const [requestChangeDialogOpen, setRequestChangeDialogOpen] = useState(false);
  const [requestChangeFormData, setRequestChangeFormData] = useState({
    request_type: 'RESCHEDULE',
    proposed_date: '',
    proposed_time: '',
    reason: '',
  });

  // Selected enrollment for viewing context (defaults to active)
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  // Update selected enrollment when active enrollment changes
  useEffect(() => {
    if (student?.active_enrollment_id && !selectedEnrollmentId) {
      setSelectedEnrollmentId(student.active_enrollment_id);
    }
  }, [student?.active_enrollment_id]);

  const loadData = async (enrollmentIdOverride = null) => {
    try {
      const [studentRes, lessonsRes, programsRes, locationsRes, coachesRes] = await Promise.all([
        studentsAPI.getById(id),
        lessonsAPI.getByStudent(id),
        programsAPI.getAll(),
        locationsAPI.getAll({}),
        coachesAPI.getAll({}),
      ]);
      setStudent(studentRes.data);
      setPrograms(programsRes.data || []);
      setLocations(locationsRes.data || []);
      setCoaches(coachesRes.data || []);
      setFormData(studentRes.data);
      
      // Load enrollment history first
      let enrollmentsList = [];
      try {
        const enrollmentsRes = await enrollmentsAPI.getByStudent(id);
        enrollmentsList = enrollmentsRes.data || [];
        setEnrollments(enrollmentsList);
      } catch (e) {
        console.log('No enrollments found');
      }
      
      // Determine which enrollment to show lessons for
      const targetEnrollmentId = enrollmentIdOverride || selectedEnrollmentId || studentRes.data.active_enrollment_id;
      
      // Set selected enrollment ID if not already set
      if (!selectedEnrollmentId && targetEnrollmentId) {
        setSelectedEnrollmentId(targetEnrollmentId);
      }
      
      // Set active enrollment object
      const active = enrollmentsList.find(e => e.id === targetEnrollmentId);
      setActiveEnrollment(active || null);
      
      // Filter lessons by selected enrollment and sort ascending by date
      const allLessons = lessonsRes.data || [];
      const enrollmentLessons = allLessons
        .filter(l => !targetEnrollmentId || l.enrollment_id === targetEnrollmentId)
        .sort((a, b) => {
          const dateA = new Date(a.scheduled_date + ' ' + (a.scheduled_time || '00:00'));
          const dateB = new Date(b.scheduled_date + ' ' + (b.scheduled_time || '00:00'));
          return dateA - dateB; // Ascending order
        });
      setLessons(enrollmentLessons);
      
      // Load lesson change requests for this student
      try {
        const requestsRes = await lessonRequestsAPI.getByStudent(id);
        setLessonRequests(requestsRes.data || []);
      } catch (e) {
        console.log('No lesson requests found');
      }
      
      // Check certificate status - SCOPED TO SELECTED ENROLLMENT
      try {
        const certRes = await certificatesAPI.checkEligibility(id, targetEnrollmentId);
        setCertificateStatus(certRes.data);
      } catch (e) {
        // Certificate may not exist yet
        setCertificateStatus(null);
      }
    } catch (error) {
      toast.error('Failed to load student data');
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (url) => {
    setStudent({ ...student, photo_url: url });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await studentsAPI.update(id, formData);
      toast.success('Student updated successfully');
      dispatchActivityRefresh();
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update student');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await studentsAPI.delete(id);
      toast.success('Student deleted successfully');
      dispatchActivityRefresh();
      navigate('/students');
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const handleCompleteLesson = async (lessonId) => {
    try {
      await lessonsAPI.updateStatus(lessonId, { status: 'completed', notes: 'Completed' });
      toast.success('Lesson marked as complete');
      dispatchActivityRefresh();
      loadData();
    } catch (error) {
      toast.error('Failed to update lesson');
    }
  };

  const handleSendLessonReminder = async (lessonId) => {
    try {
      await emailNotificationsAPI.sendLessonReminder(lessonId);
      toast.success('Reminder sent!');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  // Open edit/reschedule dialog
  const handleOpenEditLesson = (lesson) => {
    setSelectedLesson(lesson);
    setLessonFormData({
      scheduled_date: lesson.scheduled_date || '',
      scheduled_time: lesson.scheduled_time || '',
      coach_id: lesson.coach_id || '',
      location_id: lesson.location_id || '',
      notes: lesson.notes || '',
    });
    setEditLessonDialogOpen(true);
  };

  // Save edited lesson (same record, no duplication)
  const handleSaveLesson = async (e) => {
    e.preventDefault();
    if (!selectedLesson) return;
    
    setSubmitting(true);
    try {
      await lessonsAPI.update(selectedLesson.id, lessonFormData);
      toast.success(`Lesson #${selectedLesson.lesson_number} updated`);
      dispatchActivityRefresh();
      setEditLessonDialogOpen(false);
      setSelectedLesson(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lesson');
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel lesson (admin only)
  const handleCancelLesson = async () => {
    if (!selectedLesson) return;
    
    try {
      await lessonsAPI.cancel(selectedLesson.id);
      toast.success(`Lesson #${selectedLesson.lesson_number} cancelled`);
      dispatchActivityRefresh();
      setCancelLessonDialogOpen(false);
      setSelectedLesson(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel lesson');
    }
  };

  // Restore cancelled lesson (admin only)
  const handleRestoreLesson = async (lesson) => {
    try {
      await lessonsAPI.restore(lesson.id);
      toast.success(`Lesson #${lesson.lesson_number} restored`);
      dispatchActivityRefresh();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore lesson');
    }
  };

  // Delete lesson permanently (admin only)
  const handleDeleteLesson = async () => {
    if (!selectedLesson) return;
    
    try {
      await lessonsAPI.delete(selectedLesson.id);
      toast.success(`Lesson #${selectedLesson.lesson_number} deleted permanently`);
      dispatchActivityRefresh();
      setDeleteLessonDialogOpen(false);
      setSelectedLesson(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lesson');
    }
  };

  const handleBulkSchedule = async (e) => {
    e.preventDefault();
    if (!bulkFormData.start_date || !bulkFormData.coach_id || !bulkFormData.location_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    // CRITICAL: Require enrollment context for scheduling lessons
    const targetEnrollmentId = selectedEnrollmentId || student?.active_enrollment_id;
    if (!targetEnrollmentId) {
      toast.error('No enrollment selected - cannot schedule lessons');
      return;
    }

    setSubmitting(true);
    try {
      await lessonsAPI.bulkCreate({
        student_id: id,
        program_id: student.program_id,
        enrollment_id: targetEnrollmentId,  // CRITICAL: Must send enrollment_id
        ...bulkFormData,
      });
      toast.success(`${bulkFormData.lesson_count} lessons scheduled for this enrollment!`);
      dispatchActivityRefresh();
      setBulkDialogOpen(false);
      // Reload lessons for the current enrollment context
      await loadData(targetEnrollmentId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule lessons');
    } finally {
      setSubmitting(false);
    }
  };

  // Create new enrollment (re-enroll student)
  const handleCreateEnrollment = async (e) => {
    e.preventDefault();
    if (!enrollmentFormData.program_id) {
      toast.error('Please select a program');
      return;
    }

    setSubmitting(true);
    try {
      await enrollmentsAPI.create({
        student_id: id,
        program_id: enrollmentFormData.program_id,
        sessions_total: enrollmentFormData.sessions_total,
        start_date: enrollmentFormData.start_date,
        notes: enrollmentFormData.notes || `New enrollment created on ${new Date().toLocaleDateString()}`,
      });
      toast.success('New enrollment created! Student can now continue their journey.');
      dispatchActivityRefresh();
      setNewEnrollmentDialogOpen(false);
      setEnrollmentFormData({
        program_id: '',
        sessions_total: 10,
        start_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create enrollment');
    } finally {
      setSubmitting(false);
    }
  };

  // ========== LESSON CHANGE REQUEST HANDLERS (Safe Lesson Control) ==========
  
  // Coach: Submit a change request
  const handleSubmitChangeRequest = async (e) => {
    e.preventDefault();
    if (!selectedLesson || !requestChangeFormData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (requestChangeFormData.request_type === 'RESCHEDULE' && !requestChangeFormData.proposed_date) {
      toast.error('Please select a new date for rescheduling');
      return;
    }

    setSubmitting(true);
    try {
      await lessonRequestsAPI.create({
        student_id: id,
        enrollment_id: student?.active_enrollment_id || activeEnrollment?.id,
        lesson_id: selectedLesson.id,
        request_type: requestChangeFormData.request_type,
        proposed_date: requestChangeFormData.proposed_date || null,
        proposed_time: requestChangeFormData.proposed_time || null,
        reason: requestChangeFormData.reason,
      });
      toast.success('Change request submitted! An admin will review it shortly.');
      setRequestChangeDialogOpen(false);
      setSelectedLesson(null);
      setRequestChangeFormData({
        request_type: 'RESCHEDULE',
        proposed_date: '',
        proposed_time: '',
        reason: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Admin: Approve a change request
  const handleApproveRequest = async (requestId) => {
    try {
      await lessonRequestsAPI.approve(requestId);
      toast.success('Request approved and lesson updated!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  // Admin: Reject a change request
  const handleRejectRequest = async (requestId, reason) => {
    if (!reason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await lessonRequestsAPI.reject(requestId, reason);
      toast.success('Request rejected');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  // Get pending request for a lesson (if any)
  const getLessonPendingRequest = (lessonId) => {
    return lessonRequests.find(r => r.lesson_id === lessonId && r.status === 'PENDING');
  };

  // Open request change dialog for a lesson
  const openRequestChangeDialog = (lesson) => {
    setSelectedLesson(lesson);
    setRequestChangeFormData({
      request_type: 'RESCHEDULE',
      proposed_date: lesson.scheduled_date || '',
      proposed_time: lesson.scheduled_time || '',
      reason: '',
    });
    setRequestChangeDialogOpen(true);
  };

  // Switch enrollment context (clicking enrollment card)
  const handleSwitchEnrollment = async (enrollment) => {
    // Don't switch if already selected
    if (enrollment.id === selectedEnrollmentId) return;
    
    setSelectedEnrollmentId(enrollment.id);
    setActiveEnrollment(enrollment);
    
    // Reload data with the new enrollment context
    await loadData(enrollment.id);
    toast.success(`Switched to ${enrollment.program_name || 'enrollment'} context`);
  };

  // Get computed stats for the selected enrollment
  const getSelectedEnrollmentStats = () => {
    const completed = lessons.filter(l => l.status === 'completed').length;
    const scheduled = lessons.filter(l => l.status === 'scheduled' || !l.status).length;
    const missed = lessons.filter(l => l.status === 'missed').length;
    const sessionsTotal = activeEnrollment?.sessions_total || student?.lessons_total || 10;
    const remaining = Math.max(0, sessionsTotal - completed);
    // Cap progress at 100% (in case of data inconsistency)
    const progressPercent = sessionsTotal > 0 ? Math.min(100, Math.round((completed / sessionsTotal) * 100)) : 0;
    
    return {
      completed,
      scheduled,
      missed,
      remaining,
      sessionsTotal,
      progressPercent,
      isComplete: completed >= sessionsTotal
    };
  };

  const handleApproveCertificate = async () => {
    setApprovingCert(true);
    try {
      await certificatesAPI.approve(id);
      toast.success('Certificate approved!');
      setApprovalDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to approve certificate');
    } finally {
      setApprovingCert(false);
    }
  };

  const handleRevokeCertificate = async () => {
    setApprovingCert(true);
    try {
      await certificatesAPI.revoke(id);
      toast.success('Certificate revoked');
      loadData();
    } catch (error) {
      toast.error('Failed to revoke certificate');
    } finally {
      setApprovingCert(false);
    }
  };

  const handleDownloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const response = await certificatesAPI.downloadPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate_${student.name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    } finally {
      setDownloadingCert(false);
    }
  };

  // Get next scheduled lesson
  const getNextLesson = () => {
    const scheduledLessons = lessons
      .filter(l => l.status === 'scheduled')
      .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
    return scheduledLessons[0] || null;
  };

  // Get coach info for next lesson
  const getCoachForLesson = (coachId) => {
    return coaches.find(c => c.id === coachId);
  };

  // Get location info
  const getLocationForLesson = (locationId) => {
    return locations.find(l => l.id === locationId);
  };

  // Calculate attendance stats - SAFE COUNTING (excludes cancelled lessons)
  const getAttendanceStats = () => {
    const completed = lessons.filter(l => l.status === 'completed').length;
    const cancelled = lessons.filter(l => l.status === 'cancelled').length;
    const scheduled = lessons.filter(l => l.status === 'scheduled' || !l.status).length; // Treat missing status as scheduled
    const missed = lessons.filter(l => l.status === 'missed').length;
    
    // Total planned = all non-cancelled lessons
    const totalPlanned = lessons.filter(l => l.status !== 'cancelled').length;
    
    // Attendance rate = completed / (completed + missed), excluding cancelled
    const attendanceRate = (completed + missed) > 0 
      ? Math.round((completed / (completed + missed)) * 100) 
      : 100;
    
    return { completed, cancelled, scheduled, missed, total: lessons.length, totalPlanned, attendanceRate };
  };

  // Skill milestones based on progress
  const getSkillMilestones = () => {
    const progress = student?.progress_percent || 0;
    return [
      { name: 'Water Comfort', threshold: 10, icon: Waves },
      { name: 'Basic Floating', threshold: 25, icon: Heart },
      { name: 'Kicking & Movement', threshold: 40, icon: TrendingUp },
      { name: 'Arm Strokes', threshold: 60, icon: Target },
      { name: 'Full Stroke', threshold: 80, icon: Star },
      { name: 'Swim Ready', threshold: 100, icon: Trophy },
    ];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-slate-400">Loading student details...</p>
      </div>
    );
  }

  if (!student) return null;

  const enrollmentStats = getSelectedEnrollmentStats();
  const canDownloadCertificate = enrollmentStats.isComplete && certificateStatus?.certificate?.approved;
  const isPendingApproval = enrollmentStats.isComplete && certificateStatus?.certificate && !certificateStatus?.certificate?.approved;
  const nextLesson = getNextLesson();
  const attendanceStats = getAttendanceStats();
  const milestones = getSkillMilestones();
  
  // Check if user can manage students (admin/manager only, not coach)
  const canManageStudent = () => isAdmin() || (canEdit() && user?.role === 'manager');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/students')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-slate-900">{student.name}</h1>
          <p className="text-slate-500">{student.program_name}</p>
        </div>
        {canManageStudent() && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="edit-student-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            {isAdmin() && (
              <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-student-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ========== PARENT NEXT-ACTION PANEL ========== */}
      {nextLesson && (
        <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Next Lesson</h3>
                    <p className="text-sm text-slate-500">Lesson #{nextLesson.lesson_number}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <Calendar className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Date & Time</p>
                      <p className="font-semibold text-slate-900">
                        {new Date(nextLesson.scheduled_date).toLocaleDateString('en-US', { 
                          weekday: 'long', month: 'short', day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Location</p>
                      <p className="font-semibold text-slate-900">
                        {getLocationForLesson(nextLesson.location_id)?.name || student.location_name}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <User className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Coach</p>
                      <p className="font-semibold text-slate-900">
                        {nextLesson.coach_name || 'To be assigned'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <Briefcase className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">What to Bring</p>
                      <p className="font-semibold text-slate-900 text-sm">Swimsuit, Towel, Goggles</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Trust Badge */}
              <div className="flex flex-col items-center gap-2 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
                <span className="text-xs font-semibold text-emerald-700 text-center">Certified Coach</span>
                <span className="text-xs text-emerald-600 text-center">Safety-First Training</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              {canEdit() && isAdmin() ? (
                <PhotoUpload
                  currentPhotoUrl={student.photo_url}
                  entityType="student"
                  entityId={id}
                  onUploadSuccess={handlePhotoUpload}
                  size="lg"
                  className="mb-4"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-3xl mb-4 overflow-hidden ring-4 ring-primary/20">
                  {student.photo_url ? (
                    <img src={student.photo_url.startsWith('http') ? student.photo_url : `${process.env.REACT_APP_BACKEND_URL}${student.photo_url}`} alt={student.name} className="w-full h-full object-cover" />
                  ) : (
                    student.name.split(' ').map(n => n[0]).join('')
                  )}
                </div>
              )}
              <h2 className="text-xl font-semibold text-slate-900">{student.name}</h2>
              <div className="flex gap-2 mt-2 flex-wrap justify-center">
                <Badge variant={student.status === 'active' ? 'default' : 'secondary'}>
                  {student.status}
                </Badge>
                <Badge variant={student.student_type === 'adult' ? 'secondary' : 'outline'}>
                  {student.student_type === 'adult' ? 'Adult' : 'Child'}
                </Badge>
              </div>
              
              <div className="w-full mt-6 space-y-3">
                <div className="flex items-center gap-3 text-slate-600 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <User className="w-4 h-4 text-primary" />
                  <span>Age: {student.age !== null ? `${student.age} years` : 'N/A'}</span>
                </div>
                
                {/* Adult-specific contact info */}
                {student.student_type === 'adult' && (
                  <>
                    {student.phone && (
                      <div className="flex items-center gap-3 text-slate-600 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                        <Phone className="w-4 h-4 text-primary" />
                        <span>{student.phone}</span>
                      </div>
                    )}
                    {student.email && (
                      <div className="flex items-center gap-3 text-slate-600 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                        <Mail className="w-4 h-4 text-primary" />
                        <span className="truncate">{student.email}</span>
                      </div>
                    )}
                  </>
                )}
                
                <div className="flex items-center gap-3 text-slate-600 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>{student.location_name}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span>Enrolled: {student.enrollment_date}</span>
                </div>
              </div>

              {/* Guardian info for child students */}
              {student.student_type === 'child' && student.guardian_name && (
                <div className="w-full mt-6 pt-6 border-t">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-500" /> Guardian
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-3 text-left">
                    <p className="font-medium text-slate-700">{student.guardian_name}</p>
                    {student.guardian_relationship && (
                      <p className="text-slate-500 text-sm">({student.guardian_relationship})</p>
                    )}
                    {student.guardian_primary_phone && (
                      <p className="text-slate-600 text-sm flex items-center gap-1 mt-2">
                        <Phone className="w-3 h-3" /> {student.guardian_primary_phone}
                      </p>
                    )}
                    {student.guardian_email && (
                      <p className="text-slate-600 text-sm flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {student.guardian_email}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Progress & Lessons */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* ========== ENHANCED PROGRESS CARD ========== */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent border-b">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Program Progress
                  {selectedEnrollmentId !== student?.active_enrollment_id && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 ml-2">
                      Viewing: {activeEnrollment?.program_name || 'Previous Enrollment'}
                    </Badge>
                  )}
                </span>
                <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                  {attendanceStats.attendanceRate}% Attendance
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Main Progress - Scoped to selected enrollment */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-4xl font-bold text-primary">{enrollmentStats.progressPercent}%</span>
                      <span className="text-slate-500 ml-2">Complete</span>
                    </div>
                    <span className="text-slate-500 text-sm bg-slate-100 px-3 py-1 rounded-full">
                      {enrollmentStats.completed} of {enrollmentStats.sessionsTotal} lessons
                    </span>
                  </div>
                  <Progress value={enrollmentStats.progressPercent} className="h-4 rounded-full" />
                  {enrollmentStats.progressPercent === 0 && (
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      Progress will update after your first completed session
                    </p>
                  )}
                </div>
                
                {/* Skill Milestones */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    Skill Milestones
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {milestones.map((milestone, idx) => {
                      const achieved = student.progress_percent >= milestone.threshold;
                      const Icon = milestone.icon;
                      return (
                        <div 
                          key={idx}
                          className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                            achieved 
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                              : 'bg-slate-50 border-slate-200 text-slate-400'
                          }`}
                        >
                          {achieved ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <Circle className="w-5 h-5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${achieved ? 'text-emerald-700' : 'text-slate-500'}`}>
                              {milestone.name}
                            </p>
                            <p className="text-xs text-slate-400">{milestone.threshold}%</p>
                          </div>
                          <Icon className={`w-4 h-4 flex-shrink-0 ${achieved ? 'text-emerald-500' : 'text-slate-300'}`} />
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Attendance Summary - Scoped to selected enrollment */}
                <div className="grid grid-cols-4 gap-3 pt-4 border-t">
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{enrollmentStats.completed}</p>
                    <p className="text-xs text-emerald-600 font-medium">Completed</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{enrollmentStats.scheduled}</p>
                    <p className="text-xs text-amber-600 font-medium">Scheduled</p>
                  </div>
                  <div className="text-center p-3 bg-rose-50 rounded-lg">
                    <p className="text-2xl font-bold text-rose-500">{enrollmentStats.missed}</p>
                    <p className="text-xs text-rose-500 font-medium">Missed</p>
                  </div>
                  <div className="text-center p-3 bg-slate-100 rounded-lg">
                    <p className="text-2xl font-bold text-slate-600">{enrollmentStats.remaining}</p>
                    <p className="text-xs text-slate-500 font-medium">Remaining</p>
                  </div>
                </div>

                {/* ========== CERTIFICATE CELEBRATION UI - Per Enrollment ========== */}
                {enrollmentStats.isComplete && (
                  <div className="pt-4 border-t">
                    {certificateStatus?.certificate?.approved ? (
                      /* CELEBRATION STATE - Certificate Approved */
                      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 p-1">
                        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-5">
                          <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg">
                                <Trophy className="w-8 h-8 text-white" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <PartyPopper className="w-5 h-5 text-amber-500" />
                                  <h4 className="font-bold text-lg text-amber-800">Certificate Unlocked!</h4>
                                  <PartyPopper className="w-5 h-5 text-amber-500" />
                                </div>
                                <p className="text-amber-700">
                                  Congratulations on completing the program!
                                </p>
                                <p className="text-xs text-amber-600 mt-1">
                                  Approved by {certificateStatus.certificate.approved_by} on {new Date(certificateStatus.certificate.approved_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                onClick={handleDownloadCertificate} 
                                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white shadow-lg" 
                                disabled={downloadingCert}
                                data-testid="download-certificate-btn"
                              >
                                {downloadingCert ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4 mr-2" />
                                )}
                                Download Certificate
                              </Button>
                              {isAdmin() && (
                                <Button 
                                  variant="outline" 
                                  onClick={handleRevokeCertificate}
                                  disabled={approvingCert}
                                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                  data-testid="revoke-certificate-btn"
                                >
                                  {approvingCert ? <Loader2 className="w-4 h-4" /> : <ShieldX className="w-4 h-4" />}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* PENDING STATE - Awaiting Approval */
                      <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 p-5">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                              <Clock className="w-7 h-7 text-amber-600" />
                            </div>
                            <div>
                              <h4 className="font-bold text-amber-800 flex items-center gap-2">
                                <Sparkles className="w-4 h-4" />
                                Certificate In Progress
                              </h4>
                              <p className="text-amber-700 text-sm">Program complete! Awaiting admin approval.</p>
                            </div>
                          </div>
                          {isAdmin() ? (
                            <Button 
                              onClick={() => setApprovalDialogOpen(true)} 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid="approve-certificate-btn"
                            >
                              <ShieldCheck className="w-4 h-4 mr-2" />
                              Approve Certificate
                            </Button>
                          ) : (
                            <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-100 px-4 py-2">
                              <Clock className="w-3 h-3 mr-1" /> Pending Approval
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Not yet eligible */}
                {!certificateStatus?.eligible && student.progress_percent < 100 && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <Award className="w-5 h-5 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-600">Certificate Progress</p>
                        <p className="text-sm text-slate-500">
                          Complete {enrollmentStats.remaining} more lessons to earn your certificate
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary">{enrollmentStats.progressPercent}%</p>
                        <p className="text-xs text-slate-500">of 100%</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ========== ENHANCED LESSONS LIST ========== */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Lesson History
                  {/* Inline Pending Requests badge */}
                  {lessonRequests.filter(r => r.status === 'PENDING').length > 0 && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 ml-2">
                      <Clock className="w-3 h-3 mr-1" />
                      {lessonRequests.filter(r => r.status === 'PENDING').length} Pending
                    </Badge>
                  )}
                </span>
                <div className="flex gap-2">
                  {isAdmin() && lessonRequests.filter(r => r.status === 'PENDING').length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/admin/lesson-requests')}
                      className="text-amber-600 border-amber-300"
                      data-testid="view-requests-btn"
                    >
                      Review Requests
                    </Button>
                  )}
                  {canEdit() && (
                    <Button onClick={() => setBulkDialogOpen(true)} variant="outline" data-testid="bulk-schedule-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Schedule Lessons
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {lessons.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium text-slate-500">Your first lesson is coming up</p>
                    <p className="text-sm text-slate-400 mt-1">Lessons will appear here once scheduled</p>
                  </div>
                ) : (
                  lessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                        lesson.status === 'completed' 
                          ? 'bg-emerald-50/50 border-emerald-200' 
                          : lesson.status === 'cancelled'
                          ? 'bg-slate-50 border-slate-200 opacity-60'
                          : lesson.status === 'scheduled' || !lesson.status
                          ? 'bg-white border-slate-200'
                          : 'bg-rose-50/50 border-rose-200'
                      }`}
                      data-testid={`lesson-item-${lesson.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          lesson.status === 'completed' 
                            ? 'bg-emerald-100 text-emerald-600' 
                            : lesson.status === 'cancelled'
                            ? 'bg-slate-200 text-slate-400'
                            : lesson.status === 'scheduled' || !lesson.status
                            ? 'bg-amber-100 text-amber-600'
                            : 'bg-rose-100 text-rose-500'
                        }`}>
                          {lesson.status === 'completed' ? (
                            <CheckCircle className="w-6 h-6" />
                          ) : lesson.status === 'cancelled' ? (
                            <XCircle className="w-6 h-6" />
                          ) : lesson.status === 'scheduled' || !lesson.status ? (
                            <Clock className="w-6 h-6" />
                          ) : (
                            <ShieldX className="w-6 h-6" />
                          )}
                        </div>
                        <div>
                          <p className={`font-semibold ${lesson.status === 'cancelled' ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                            Lesson #{lesson.lesson_number}
                          </p>
                          <p className="text-sm text-slate-500">
                            {new Date(lesson.scheduled_date).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                            {lesson.scheduled_time && ` at ${lesson.scheduled_time}`}
                          </p>
                          <p className="text-xs text-slate-400">
                            Coach: {lesson.coach_name || 'TBA'} • {lesson.location_name || 'TBA'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          lesson.status === 'completed' ? 'default' : 
                          lesson.status === 'cancelled' ? 'outline' :
                          lesson.status === 'scheduled' || !lesson.status ? 'secondary' : 'destructive'
                        } className={`capitalize ${lesson.status === 'cancelled' ? 'text-slate-400' : ''}`}>
                          {lesson.status || 'scheduled'}
                        </Badge>
                        
                        {/* Action buttons based on status and role */}
                        {lesson.status === 'cancelled' && isAdmin() && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRestoreLesson(lesson)}
                            title="Restore Lesson"
                            className="h-8"
                            data-testid={`restore-lesson-${lesson.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                        )}
                        
                        {(lesson.status === 'scheduled' || !lesson.status) && (
                          <>
                            {canEdit() && (
                              <Button
                                size="sm"
                                onClick={() => handleCompleteLesson(lesson.id)}
                                className="h-8"
                                data-testid={`complete-lesson-${lesson.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                            )}
                            
                            {/* Coach: Request Change button (cannot edit directly) */}
                            {!isAdmin() && lesson.status !== 'completed' && lesson.status !== 'cancelled' && (
                              <>
                                {getLessonPendingRequest(lesson.id) ? (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Request Pending
                                  </Badge>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => openRequestChangeDialog(lesson)}
                                    data-testid={`request-change-${lesson.id}`}
                                  >
                                    <MessageSquare className="w-4 h-4 mr-1" />
                                    Request Change
                                  </Button>
                                )}
                              </>
                            )}
                            
                            {/* Admin-only actions dropdown */}
                            {isAdmin() && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`lesson-actions-${lesson.id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenEditLesson(lesson)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenEditLesson(lesson)}>
                                    <CalendarClock className="w-4 h-4 mr-2" />
                                    Reschedule
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleSendLessonReminder(lesson.id)}
                                  >
                                    <Mail className="w-4 h-4 mr-2" />
                                    Send Reminder
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setSelectedLesson(lesson);
                                      setCancelLessonDialogOpen(true);
                                    }}
                                    className="text-amber-600"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Cancel Lesson
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setSelectedLesson(lesson);
                                      setDeleteLessonDialogOpen(true);
                                    }}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Permanently
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </>
                        )}
                      </div>
                      </div>
                      
                      {/* Show pending request badge for this lesson */}
                      {getLessonPendingRequest(lesson.id) && isAdmin() && (
                        <div className="mt-2 p-2 bg-amber-50 rounded-md border border-amber-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                              <span className="text-amber-700">
                                <strong>{getLessonPendingRequest(lesson.id).request_type}</strong> requested by {getLessonPendingRequest(lesson.id).created_by_name}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => handleApproveRequest(getLessonPendingRequest(lesson.id).id)}
                                data-testid={`approve-request-${lesson.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => {
                                  const reason = prompt('Rejection reason:');
                                  if (reason) handleRejectRequest(getLessonPendingRequest(lesson.id).id, reason);
                                }}
                                data-testid={`reject-request-${lesson.id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-amber-600 mt-1">Reason: {getLessonPendingRequest(lesson.id).reason}</p>
                        </div>
                      )}
                    </div>
                  ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* ========== ENROLLMENT HISTORY (Phase 3 & 4) ========== */}
        <Card className="border-2 border-dashed border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <History className="w-5 h-5 text-primary" />
                  Enrollment History
                </span>
                {isAdmin() && (
                  <Button 
                    onClick={() => setNewEnrollmentDialogOpen(true)} 
                    variant="outline" 
                    size="sm"
                    data-testid="new-enrollment-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    New Enrollment
                  </Button>
                )}
              </CardTitle>
              <CardDescription>
                Track course participation history. Students can re-enroll without losing previous records.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {enrollments.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium text-slate-500">No enrollment history yet</p>
                  <p className="text-sm text-slate-400 mt-1">Enrollment records will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {enrollments.map((enrollment, idx) => (
                    <div 
                      key={enrollment.id}
                      onClick={() => handleSwitchEnrollment(enrollment)}
                      className={`p-4 rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${
                        enrollment.id === selectedEnrollmentId
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                          : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                      }`}
                      data-testid={`enrollment-${enrollment.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-slate-900">
                              {enrollment.program_name || 'Program'}
                            </h4>
                            {enrollment.id === selectedEnrollmentId && (
                              <Badge variant="default" className="bg-primary">Viewing</Badge>
                            )}
                            {enrollment.id === student?.active_enrollment_id && enrollment.id !== selectedEnrollmentId && (
                              <Badge variant="outline" className="text-primary border-primary">Active</Badge>
                            )}
                            {enrollment.is_legacy && (
                              <Badge variant="outline" className="text-slate-500 text-xs">Legacy</Badge>
                            )}
                            <Badge variant={
                              enrollment.status === 'completed' ? 'default' :
                              enrollment.status === 'active' ? 'secondary' :
                              'outline'
                            } className="capitalize">
                              {enrollment.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500 text-xs">Started</p>
                              <p className="font-medium">{enrollment.start_date}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Sessions</p>
                              <p className="font-medium">{enrollment.sessions_completed}/{enrollment.sessions_total}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Progress</p>
                              <p className="font-medium">{enrollment.progress_percent}%</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Status</p>
                              <Progress value={enrollment.progress_percent} className="h-2 mt-1" />
                            </div>
                          </div>
                          {enrollment.notes && (
                            <p className="text-xs text-slate-400 mt-2 italic">{enrollment.notes}</p>
                          )}
                          {enrollment.id !== selectedEnrollmentId && (
                            <p className="text-xs text-primary mt-2 font-medium">Click to view this enrollment's lessons</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New Enrollment Dialog (Phase 4: Re-enrollment) */}
      <Dialog open={newEnrollmentDialogOpen} onOpenChange={setNewEnrollmentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary" />
              Create New Enrollment
            </DialogTitle>
            <DialogDescription>
              Re-enroll {student?.name} in a program. Previous enrollment history will be preserved.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateEnrollment} className="space-y-4">
            <div className="space-y-2">
              <Label>Program *</Label>
              <Select
                value={enrollmentFormData.program_id}
                onValueChange={(value) => setEnrollmentFormData({ ...enrollmentFormData, program_id: value })}
              >
                <SelectTrigger data-testid="enrollment-program">
                  <SelectValue placeholder="Select program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Sessions *</Label>
                <Input
                  type="number"
                  value={enrollmentFormData.sessions_total}
                  onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, sessions_total: parseInt(e.target.value) || 10 })}
                  min={1}
                  max={100}
                  data-testid="enrollment-sessions"
                />
              </div>
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={enrollmentFormData.start_date}
                  onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, start_date: e.target.value })}
                  data-testid="enrollment-start-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={enrollmentFormData.notes}
                onChange={(e) => setEnrollmentFormData({ ...enrollmentFormData, notes: e.target.value })}
                placeholder="e.g., Returning student, wants to refresh skills"
                data-testid="enrollment-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewEnrollmentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="confirm-enrollment">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Enrollment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Request Change Dialog (Coach: Request Reschedule/Cancel) */}
      <Dialog open={requestChangeDialogOpen} onOpenChange={setRequestChangeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Request Lesson Change
            </DialogTitle>
            <DialogDescription>
              Submit a request to modify Lesson #{selectedLesson?.lesson_number}. An admin will review and approve or reject your request.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitChangeRequest} className="space-y-4">
            {/* Current lesson info */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Current:</strong> {selectedLesson?.scheduled_date} at {selectedLesson?.scheduled_time || 'TBD'}
              </p>
            </div>
            
            {/* Request type selection */}
            <div className="space-y-3">
              <Label>What would you like to do? *</Label>
              <RadioGroup 
                value={requestChangeFormData.request_type}
                onValueChange={(value) => setRequestChangeFormData({...requestChangeFormData, request_type: value})}
                className="flex flex-col gap-3"
              >
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                  <RadioGroupItem value="RESCHEDULE" id="reschedule" data-testid="request-type-reschedule" />
                  <Label htmlFor="reschedule" className="flex-1 cursor-pointer">
                    <div className="font-medium">Reschedule</div>
                    <p className="text-sm text-slate-500">Move to a different date/time</p>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                  <RadioGroupItem value="CANCEL" id="cancel" data-testid="request-type-cancel" />
                  <Label htmlFor="cancel" className="flex-1 cursor-pointer">
                    <div className="font-medium">Cancel</div>
                    <p className="text-sm text-slate-500">Student cannot attend</p>
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {/* Proposed new date/time (only for RESCHEDULE) */}
            {requestChangeFormData.request_type === 'RESCHEDULE' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>New Date *</Label>
                  <Input
                    type="date"
                    value={requestChangeFormData.proposed_date}
                    onChange={(e) => setRequestChangeFormData({...requestChangeFormData, proposed_date: e.target.value})}
                    data-testid="request-proposed-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Time</Label>
                  <Input
                    type="time"
                    value={requestChangeFormData.proposed_time}
                    onChange={(e) => setRequestChangeFormData({...requestChangeFormData, proposed_time: e.target.value})}
                    data-testid="request-proposed-time"
                  />
                </div>
              </div>
            )}
            
            {/* Reason (required) */}
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={requestChangeFormData.reason}
                onChange={(e) => setRequestChangeFormData({...requestChangeFormData, reason: e.target.value})}
                placeholder={requestChangeFormData.request_type === 'RESCHEDULE' 
                  ? "e.g., Student has a school event on this date"
                  : "e.g., Student is feeling unwell"
                }
                rows={3}
                data-testid="request-reason"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRequestChangeDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-request-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Send className="w-4 h-4 mr-2" />
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Schedule Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Multiple Lessons</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Number of Lessons *</Label>
                <Input
                  type="number"
                  value={bulkFormData.lesson_count}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, lesson_count: parseInt(e.target.value) })}
                  min={1}
                  max={52}
                  data-testid="bulk-lesson-count"
                />
              </div>
              <div className="space-y-2">
                <Label>Interval (days) *</Label>
                <Input
                  type="number"
                  value={bulkFormData.interval_days}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, interval_days: parseInt(e.target.value) })}
                  min={1}
                  data-testid="bulk-interval"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={bulkFormData.start_date}
                onChange={(e) => setBulkFormData({ ...bulkFormData, start_date: e.target.value })}
                data-testid="bulk-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Coach *</Label>
              <Select
                value={bulkFormData.coach_id}
                onValueChange={(value) => setBulkFormData({ ...bulkFormData, coach_id: value })}
              >
                <SelectTrigger data-testid="bulk-coach">
                  <SelectValue placeholder="Select coach" />
                </SelectTrigger>
                <SelectContent>
                  {coaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id}>{coach.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Location *</Label>
              <Select
                value={bulkFormData.location_id}
                onValueChange={(value) => setBulkFormData({ ...bulkFormData, location_id: value })}
              >
                <SelectTrigger data-testid="bulk-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkSchedule} disabled={submitting} data-testid="confirm-bulk-schedule">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Schedule {bulkFormData.lesson_count} Lessons
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog - Mirrors Add Student Form */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Student</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            {/* Student Type - Locked/Read-only */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Student Type</Label>
              <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                <Badge variant={formData.student_type === 'adult' ? 'secondary' : 'outline'}>
                  {formData.student_type === 'adult' ? 'Adult' : 'Child'}
                </Badge>
                <span className="text-sm text-slate-500">(Cannot be changed)</span>
              </div>
            </div>

            {/* Personal Information */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-700 mb-3">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name || ''}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    data-testid="edit-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name || ''}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    data-testid="edit-last-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    data-testid="edit-dob"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sex</Label>
                  <Select
                    value={formData.sex || ''}
                    onValueChange={(value) => setFormData({ ...formData, sex: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sex" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Age (Calculated)</Label>
                  <Input
                    value={formData.age !== null ? formData.age : 'N/A'}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status || 'active'}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="graduated">Graduated</SelectItem>
                      <SelectItem value="withdrawn">Withdrawn</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Child-only Fields */}
            {formData.student_type === 'child' && (
              <div className="border-t pt-4">
                <h3 className="font-semibold text-slate-700 mb-3">Guardian Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Grade/Class</Label>
                    <Input
                      value={formData.grade || ''}
                      onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                      placeholder="e.g., Grade 3"
                      data-testid="edit-grade"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationship</Label>
                    <Select
                      value={formData.guardian_relationship || ''}
                      onValueChange={(value) => setFormData({ ...formData, guardian_relationship: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mother">Mother</SelectItem>
                        <SelectItem value="Father">Father</SelectItem>
                        <SelectItem value="Guardian">Guardian</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Guardian Name *</Label>
                    <Input
                      value={formData.guardian_name || ''}
                      onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                      data-testid="edit-guardian-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Guardian Phone *</Label>
                    <Input
                      value={formData.guardian_primary_phone || ''}
                      onChange={(e) => setFormData({ ...formData, guardian_primary_phone: e.target.value })}
                      data-testid="edit-guardian-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Guardian Email</Label>
                    <Input
                      type="email"
                      value={formData.guardian_email || ''}
                      onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                      data-testid="edit-guardian-email"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Adult-only Fields */}
            {formData.student_type === 'adult' && (
              <div className="border-t pt-4">
                <h3 className="font-semibold text-slate-700 mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="edit-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="edit-phone"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Program Information */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-700 mb-3">Program Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Program</Label>
                  <Select
                    value={formData.program_id || ''}
                    onValueChange={(value) => setFormData({ ...formData, program_id: value })}
                  >
                    <SelectTrigger data-testid="edit-program">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Location</Label>
                  <Select
                    value={formData.location_id || ''}
                    onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                  >
                    <SelectTrigger data-testid="edit-location">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Start Date (Editable)</Label>
                  <Input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    data-testid="edit-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Enrollment Date (Read-only)</Label>
                  <Input
                    value={formData.enrollment_date || ''}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-student-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Student</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {student.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Certificate Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Are you sure you want to approve the certificate for {student.name}?</p>
            <p className="text-sm text-slate-500">
              Once approved, the student will be able to download their official certificate.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleApproveCertificate} 
              disabled={approvingCert}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {approvingCert && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Approve Certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Reschedule Lesson Dialog */}
      <Dialog open={editLessonDialogOpen} onOpenChange={setEditLessonDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedLesson ? `Edit Lesson #${selectedLesson.lesson_number}` : 'Edit Lesson'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveLesson} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={lessonFormData.scheduled_date}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, scheduled_date: e.target.value })}
                  required
                  data-testid="edit-lesson-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={lessonFormData.scheduled_time || ''}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, scheduled_time: e.target.value })}
                  data-testid="edit-lesson-time"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Coach *</Label>
              <Select
                value={lessonFormData.coach_id}
                onValueChange={(value) => setLessonFormData({ ...lessonFormData, coach_id: value })}
              >
                <SelectTrigger data-testid="edit-lesson-coach">
                  <SelectValue placeholder="Select coach" />
                </SelectTrigger>
                <SelectContent>
                  {coaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id}>{coach.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Location *</Label>
              <Select
                value={lessonFormData.location_id}
                onValueChange={(value) => setLessonFormData({ ...lessonFormData, location_id: value })}
              >
                <SelectTrigger data-testid="edit-lesson-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={lessonFormData.notes || ''}
                onChange={(e) => setLessonFormData({ ...lessonFormData, notes: e.target.value })}
                placeholder="Optional notes"
                data-testid="edit-lesson-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditLessonDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-lesson-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Lesson Confirmation */}
      <AlertDialog open={cancelLessonDialogOpen} onOpenChange={setCancelLessonDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Lesson?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark Lesson #{selectedLesson?.lesson_number} as cancelled. 
              The lesson will be excluded from totals and attendance calculations.
              You can restore it later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Lesson</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelLesson}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-cancel-lesson"
            >
              Cancel Lesson
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Lesson Confirmation (Permanent) */}
      <AlertDialog open={deleteLessonDialogOpen} onOpenChange={setDeleteLessonDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Permanently Delete Lesson?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete Lesson #{selectedLesson?.lesson_number}. 
              <strong className="text-red-600"> This action cannot be undone.</strong>
              <br /><br />
              Consider using "Cancel" instead if you want to keep the record for audit purposes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Lesson</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteLesson}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-lesson"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StudentDetail;
